import React from 'react';
import { useTranslation } from 'react-i18next';
import { landingPageFeatures } from '../../constants/landingPageData';
import OptimizedImage from '../common/OptimizedImage';

// Import feature images
import bibleAiChatImage from '../../assets/bible-ai-chat.webp';
import bibleStudyProgressImage from '../../assets/bible-study-progress.webp';
import bookLibraryImage from '../../assets/book-library.webp';
import favBibleVersesImage from '../../assets/fav-bible-verses.webp';
import growthProfileImage from '../../assets/growth-profile.webp';
import readingPlansImage from '../../assets/reading-plans.webp';
import journalNotesImage from '../../assets/journal-and-notes.webp';
import bibleVersions from '../../assets/bible-versions.webp';
import appAssistantAiImage from '../../assets/app-assistant-ai.webp';
import mediaAndLearningImage from '../../assets/media-and-learning.webp';
import biblicalTheologyProfileImage from '../../assets/biblical-theology-profile.webp';

const FeaturesSection: React.FC = () => {
  const { t } = useTranslation();
  
  // Create features array with translations
  const features = [
    {
      title: t('features.feature1.title'),
      description: t('features.feature1.description'),
      icon: '💬',
      benefits: [
        t('features.feature1.benefit1'),
        t('features.feature1.benefit2'),
        t('features.feature1.benefit3')
      ]
    },
    {
      title: t('features.feature2.title'),
      description: t('features.feature2.description'),
      icon: '📈',
      benefits: [
        t('features.feature2.benefit1'),
        t('features.feature2.benefit2'),
        t('features.feature2.benefit3')
      ]
    },
    {
      title: t('features.feature3.title'),
      description: t('features.feature3.description'),
      icon: '📖',
      benefits: [
        t('features.feature3.benefit1'),
        t('features.feature3.benefit2'),
        t('features.feature3.benefit3')
      ]
    },
    {
      title: t('features.feature4.title'),
      description: t('features.feature4.description'),
      icon: '🛡️',
      benefits: [
        t('features.feature4.benefit1'),
        t('features.feature4.benefit2'),
        t('features.feature4.benefit3')
      ]
    },
    {
      title: t('features.feature5.title'),
      description: t('features.feature5.description'),
      icon: '⏰',
      benefits: [
        t('features.feature5.benefit1'),
        t('features.feature5.benefit2'),
        t('features.feature5.benefit3')
      ]
    },
    {
      title: t('features.feature6.title'),
      description: t('features.feature6.description'),
      icon: '📊',
      benefits: [
        t('features.feature6.benefit1'),
        t('features.feature6.benefit2'),
        t('features.feature6.benefit3')
      ]
    },
    {
      title: t('features.feature7.title'),
      description: t('features.feature7.description'),
      icon: '📝',
      benefits: [
        t('features.feature7.benefit1'),
        t('features.feature7.benefit2'),
        t('features.feature7.benefit3')
      ]
    },
    {
      title: t('features.feature8.title'),
      description: t('features.feature8.description'),
      icon: '💖',
      benefits: [
        t('features.feature8.benefit1'),
        t('features.feature8.benefit2'),
        t('features.feature8.benefit3')
      ]
    },
    {
      title: t('features.feature9.title'),
      description: t('features.feature9.description'),
      icon: '🤖',
      benefits: [
        t('features.feature9.benefit1'),
        t('features.feature9.benefit2'),
        t('features.feature9.benefit3')
      ]
    },
    {
      title: t('features.feature10.title'),
      description: t('features.feature10.description'),
      icon: '🎬',
      benefits: [
        t('features.feature10.benefit1'),
        t('features.feature10.benefit2'),
        t('features.feature10.benefit3')
      ]
    },
    {
      title: t('features.feature11.title'),
      description: t('features.feature11.description'),
      icon: '📜',
      benefits: [
        t('features.feature11.benefit1'),
        t('features.feature11.benefit2'),
        t('features.feature11.benefit3')
      ]
    }
  ];
  
  // Map features to their corresponding images
  const getFeatureImage = (index: number) => {
    const images = [
      bibleAiChatImage,
      growthProfileImage,
      bibleVersions,
      bookLibraryImage,
      readingPlansImage,
      bibleStudyProgressImage,
      journalNotesImage,
      favBibleVersesImage,
      appAssistantAiImage,
      mediaAndLearningImage,
      biblicalTheologyProfileImage
    ];
    return images[index] || null;
  };

  return (
    <section className="py-20 bg-gray-50">
      <div className="container mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            {t('features.title')}
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent block">
              {t('features.titleHighlight')}
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('features.subtitle')}
          </p>
        </div>


        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => {
            const featureImage = getFeatureImage(index);
            
            return (
              <div
                key={index}
                className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 border border-gray-100"
              >
                {/* Feature Image Preview */}
                {featureImage && (
                  <div className="relative h-48 overflow-hidden bg-gradient-to-br from-blue-50 to-indigo-100">
                    <div className="absolute inset-0 bg-gradient-to-t from-white/20 to-transparent z-10"></div>
                    <OptimizedImage
                      src={featureImage}
                      alt={`${feature.title} Preview`}
                      className="group-hover:scale-110 transition-transform duration-500 filter drop-shadow-lg"
                      sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                    />
                    {/* Overlay Icon */}
                    <div className="absolute top-4 right-4 w-12 h-12 bg-white/90 backdrop-blur-sm rounded-xl flex items-center justify-center text-2xl shadow-lg z-20">
                      {feature.icon}
                    </div>
                  </div>
                )}
                
                <div className="p-6">
                  {/* Title */}
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                    {feature.title}
                  </h3>
                  
                  {/* Description */}
                  <p className="text-gray-600 mb-4 leading-relaxed text-sm">
                    {feature.description}
                  </p>
                  
                  {/* Benefits List */}
                  <ul className="space-y-2">
                    {feature.benefits.map((benefit, benefitIndex) => (
                      <li
                        key={benefitIndex}
                        className="flex items-center text-xs text-gray-500 group-hover:text-gray-700 transition-colors"
                      >
                        <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-2 group-hover:bg-blue-600 transition-colors"></span>
                        {benefit}
                      </li>
                    ))}
                  </ul>
                  
                  {/* "See in Action" Badge */}
                  {/* <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="flex items-center text-blue-600 text-xs font-semibold">
                      <span className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></span>
                      {t('features.liveInDashboard')}
                    </div>
                  </div> */}
                </div>
              </div>
            );
          })}
        </div>

          {/* Video Section */}
        <div className="mb-16 max-w-7xl mx-auto mt-16">
          <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
            <iframe
              className="absolute top-0 left-0 w-full h-full rounded-2xl shadow-2xl"
              src="https://www.youtube.com/embed/RsieHu2XW6o?start=1169"
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            ></iframe>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">
              {t('features.bottomCTA.title')}
            </h3>
            <p className="text-blue-100 mb-6">
              {t('features.bottomCTA.subtitle')}
            </p>
            <a
              href="https://christreignsmedia.io/register"
              className="bg-white text-blue-600 px-8 py-3 rounded-xl font-semibold hover:bg-gray-100 transition-colors inline-block"
            >
              {t('features.bottomCTA.button')}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;